import React from "react";
import { useSelector } from "react-redux";
import { Navigate, Outlet } from "react-router-dom";

import { hasPermissionString } from "./helpers";
import { StoreType } from "./types";

type UserData = StoreType["user"]["userData"];

/** Boolean company settings on userData, e.g. showCrossSkilling. */
type CompanyFlag = {
	[K in keyof UserData]: UserData[K] extends boolean ? K : never;
}[keyof UserData];

interface RequirePermissionProps {
	/** Dynamic permission code, e.g. SETTINGS_PERMISSIONS.WORKCENTERS.VIEW. */
	permission: string;
	/**
	 * Company add-on the page also depends on. Pass the same flag the drawer
	 * checks for the menu entry, or the page stays reachable by URL after the
	 * add-on is switched off.
	 */
	flag?: CompanyFlag;
	/** Where to send a user who lacks the permission. */
	redirectTo?: string;
}

/**
 * Route wrapper that keeps a page unreachable by URL for users who cannot see
 * its menu entry. Hiding the nav item alone is not access control — without
 * this the address bar is still a way in.
 *
 * Safe to render on a cold refresh: PermissionsGate (via PrivateRoute) holds
 * every supervisor route until the permission set has loaded, so the check
 * never sees a not-yet-fetched empty array and bounces an allowed user.
 * Company flags are restored from the JWT by setUser before that, in both
 * private-route and fetch-token.
 */
const RequirePermission = ({
	permission,
	flag,
	redirectTo = "/",
}: RequirePermissionProps) => {
	const permissions = useSelector(
		(state: StoreType) => state.user?.userData?.permissions
	);
	const isFlagEnabled = useSelector((state: StoreType) =>
		flag ? state.user?.userData?.[flag] === true : true
	);

	if (!isFlagEnabled || !hasPermissionString(permissions, permission)) {
		return <Navigate to={redirectTo} replace />;
	}

	return <Outlet />;
};

export default RequirePermission;
